import { updatePassword as authUpdatePassword } from '../../auth-actions';
import type { Database } from '../../database.types';
import { getSupabaseClient, type DdmSupabaseClient } from '../../supabase';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type UserRow = Database['public']['Tables']['users']['Row'];
type UserUpdate = Database['public']['Tables']['users']['Update'];

// CHECK users_language_check IN ('en','fr','ar') — 002_users.sql line 53.
// Local literal union (no DB enum exists).
export type LanguagePreference = 'en' | 'fr' | 'ar';

export type UpdateProfileInput = {
  userId: string;
  // first_name / last_name are NOT NULL in DB (002_users.sql) — Zod schema
  // must require min length 1 before reaching this layer.
  first_name?: string;
  last_name?: string;
  // phone is nullable in DB (users.phone TEXT NULL).
  phone?: string | null;
  language_preference?: LanguagePreference;
};

// ---------------------------------------------------------------------------
// updateProfile — UPDATE users for personal info + language preference
// ---------------------------------------------------------------------------
// RLS: `users_update_self` (002_users.sql) requires `auth.uid() = id`. The
// `email` column is read-only via this path — email changes must go through
// `auth.updateUser({email})` which triggers a Supabase Auth re-verify flow.
//
// `phone` validated by Zod upstream (E.164 regex). Empty strings normalised
// to NULL to match the DB column semantics.

export async function updateProfile(
  input: UpdateProfileInput,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<UserRow> {
  const update: UserUpdate = {};
  if (input.first_name !== undefined) update.first_name = input.first_name;
  if (input.last_name !== undefined) update.last_name = input.last_name;
  if (input.phone !== undefined) update.phone = input.phone || null;
  if (input.language_preference !== undefined) {
    update.language_preference = input.language_preference;
  }

  const { data, error } = await client
    .from('users')
    .update(update)
    .eq('id', input.userId)
    .select('*')
    .single();

  if (error) throw error;
  return data as UserRow;
}

// ---------------------------------------------------------------------------
// requestEmailChange — wrap auth.updateUser({email}) (Supabase re-verify flow)
// ---------------------------------------------------------------------------
// `public.users.email` stays the OLD address until the user clicks the
// confirmation link sent by Supabase Auth to the NEW one. The UI should
// surface a pending banner during this window.

export async function requestEmailChange(
  newEmail: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<void> {
  const { error } = await client.auth.updateUser({ email: newEmail });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// changePassword — re-export auth-actions.updatePassword (ADR-025 §G)
// ---------------------------------------------------------------------------
// Supabase Auth does NOT validate the current password, so the UI form
// shows ONE input (newPassword) only. The decision is traced in ADR-025.

export async function changePassword(
  newPassword: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<void> {
  await authUpdatePassword(newPassword, client);
}
